import { isShapeToolObject } from '@editor/global/shapeTool';
import { isDesktopOperatingSystem } from '@repo/detection';
import env from '@repo/env';
import {
  buildImageProxyUrl,
  isSVGImage,
  isUnsplashImage,
} from '@repo/utils/url';

export const AI_ART_PREFIX = 'api/aiart/';

// URL of CMS object storage
export const CMS_STORAGE_URL =
  process.env.REACT_APP_CMS_IMAGE_URL ||
  process.env.STORYBOOK_CMS_IMAGE_URL ||
  process.env.NEXT_PUBLIC_CMS_IMAGE_URL;

export const API_STORAGE_URL = process.env.REACT_APP_API_STORAGE_URL;

const REMBG_URL =
  process.env.REACT_APP_REMBG_URL || process.env.NEXT_PUBLIC_REMBG_URL;

export const LIVE_SERVER_WEBSOCKET =
  process.env.REACT_APP_LIVE_SERVER_WEBSOCKET || '';

export const UNSPLASH_PHOTO_WIDTH = 264;

export const isAiArt = (objectName: string): boolean =>
  objectName.startsWith(AI_ART_PREFIX);

export const isUserUpload = (objectName: string): boolean =>
  objectName.startsWith('api/users/');

export const isCMSElement = (objectName: string): boolean =>
  objectName.startsWith('elements/');

export const isUserUploadedElement = (objectName: string): boolean =>
  objectName.startsWith('elements/upload/');

export const isBackgroundImage = (objectName: string): boolean =>
  objectName.startsWith('elements/background/');

export const isTexture = (objectName: string): boolean =>
  objectName.startsWith('elements/texture/');

// Generated image via the use of erase & fill
export const isInpaintedImage = (objectName: string): boolean =>
  objectName.startsWith('api/generators/inpainting/');

export const isAiGeneratedElement = (objectName: string): boolean =>
  objectName.startsWith('elements/aiGenerated') ||
  objectName.startsWith('elements/aiImages');

/**
 * Builds a URL for the vectorizing server.
 * @param targetWidth The target width to resize the image to. Aspect ratio is kept.
 */
export const buildVectorizingUrl = (
  objectName: string,
  targetWidth: number
): string => {
  if (!isUnsplashImage(objectName)) {
    return `${env.imageProxyUrl}/pr:default/rs:fit:${Math.round(
      targetWidth
    )}:0/q:100/plain/${objectName}`;
  }

  return `${objectName}&w=${targetWidth}&fit=clip`;
};

/**
 * builds an absolute URL from the given objectName
 * @param {string} objectName
 */
export const buildElementUrl = (objectName: string): string => {
  // If it's a shape tool object, we return as it is
  if (isShapeToolObject(objectName)) {
    return objectName;
  }

  if (!isUnsplashImage(objectName)) {
    const cdnUrl = `${env.imageProxyUrl}/pr:sharp/plain/${objectName}`;
    return cdnUrl;
  }

  // We're asking for a Unsplash URL, objectName should be just the url

  // But for iPad/Tablet/Mobile we restrict dimensions
  if (!isDesktopOperatingSystem()) {
    return `${objectName}&w=${2048}&h=${2048}&fit=clip`;
  }

  return objectName;
};

/**
 * Builds source and preview using objectName
 * @param {string} objectName
 */
export const buildSources = (
  objectName: string
): { src: string; previewSrc: string | null } => {
  if (!objectName) {
    return { src: '', previewSrc: '' };
  }

  // In a test environment, its useful to use data URLs directly
  // to not have to deal with loading images from the network
  if (env.nodeEnv === 'test' && objectName.startsWith('data:')) {
    return {
      src: objectName,
      previewSrc: null,
    };
  }

  const src = buildElementUrl(objectName);
  const isUpload = isUserUpload(objectName);

  // we skip previews for svg uploads, to avoid issues with `trimSVG`
  if (isUpload && isSVGImage(objectName)) {
    return { src, previewSrc: null };
  }

  // it is important to get the same src for the preview as the src used to display the element in the UI
  const previewSrc = buildImageProxyUrl(objectName, {
    trimSVG: false,
    width: isUnsplashImage(objectName) ? UNSPLASH_PHOTO_WIDTH : undefined,
  });

  return { src, previewSrc };
};

type RemBGModel =
  | 'u2net'
  | 'u2netp'
  | 'u2net_human_seg'
  | 'u2net_cloth_seg'
  | 'silueta';

type RemBGParams = {
  model?: RemBGModel;
  om?: boolean;
  ppm?: boolean;
};

const DEFAULT_REMBG_PARAMS: RemBGParams = {
  model: 'u2net',
  om: false,
  ppm: false,
};

/**
 * Get the url to service that removes backgrounds
 * NOTE: params a, af, ab and ae are not used.
 * @param {string} url - url of the image to remove the background from
 * @param {boolean} params.om - only mask, we never want this as it's not really usable in the editor
 * @param {boolean} params.ppm - post process mask - usually just crops the mask, removing the blurry parts
 * @param {RemBGModel} params.model - model to use
 * @returns {string} - the resulting url
 */
export const getRemBGUrl = (
  url: string,
  params: RemBGParams = DEFAULT_REMBG_PARAMS
): string => {
  const { model, om, ppm } = { ...DEFAULT_REMBG_PARAMS, ...params };

  return `${REMBG_URL}/api/remove?url=${encodeURIComponent(
    url
  )}&model=${model}&a=false&af=240&ab=10&ae=10&om=${om}&ppm=${ppm}`;
};

/**
 * Deletes query param entries from urlSearchParams and the search bar
 * @param urlParams A valid URLSearchParams object
 * @param entries Entries to be deleted
 */
export const cleanQueryParams = (
  urlParams: URLSearchParams,
  entries: string[]
): void => {
  entries.forEach((entry: string) => urlParams.delete(entry));

  const searchString = urlParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    searchString.length ? `?${searchString}` : window.location.pathname
  );
};
